import React, { useMemo } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { ErrorMessage, Field, Formik, Form as FormikForm } from 'formik';
import { t } from 'i18next';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { upperFirst } from 'lodash';

import RequiredAstric from '../../../components/DynamicForm/required-astric';

import { setAllopathysDetails } from '../../../redux/allopathys/slice';
import { createAllopathy, updateAllopathy } from '../../../redux/allopathys';
import { commaSeparatedAllopathyTypes } from './hook';
import { getAllopathyValidationSchema } from './validation';


const AllopathyModel = ({ show, close, fetch, type, title }) => {
	const { details } = useSelector(state => state.allopathys);
	const dispatch = useDispatch();
	const isEdit = useMemo(() => Boolean(details?.id), [details]);

	const handleClose = () => {
		close();
		dispatch(setAllopathysDetails(null));
	};

	const handleSubmit = async(values, { setSubmitting, resetForm }) => {
		try {
			const action = isEdit && details.id
				? updateAllopathy({ body: values, id: details.id, type })
				: createAllopathy({
					body: values,
					type,
				});

			await dispatch(action).unwrap();
			handleClose();
			resetForm();
			fetch();
		} catch (error) {
			toast.error(error.message);
			return error;
		} finally {
			setSubmitting(false);
		}
	};

	const getModelTitle = useMemo(() => {
		if ('timing' === type) {
			return t('ALLOPATHY.NAV.TIMING');
		} else if (commaSeparatedAllopathyTypes.includes(type) && !isEdit) {
			return t(`ALLOPATHY.MODEL.${type.toUpperCase()}`);
		}
		return t('ALLOPATHY.TABLE.NAME', { title: upperFirst(title) });
	}, [type, title, isEdit, t]);

	const getNameFieldLabel = useMemo(() => {
		if (['advice', 'diet-exerise'].includes(type)) {
			return t('ALLOPATHY.TABLE.TITLE');
		} else if (commaSeparatedAllopathyTypes.includes(type)) {
			const value = type.toUpperCase();
			return isEdit ? t(`ALLOPATHY.${type === 'timing' ? 'MODEL': 'NAV'}.${value}`) : t(`ALLOPATHY.MODEL.${value}`);
		} else {
			return t('GENERAL.NAME');
		}
	}, [type, isEdit, t]);

	return (
		<Modal show={show} onHide={handleClose} backdrop="static" centered >
			<Modal.Header closeButton>
				<Modal.Title>{t(`GENERAL.${isEdit ? 'UPDATE' : 'ADD'}`)} {getModelTitle}</Modal.Title>
			</Modal.Header>

			<Formik
				initialValues={{
					name: details?.name || '',
					...(['advice', 'diet-exerise'].includes(type) ? { advice: details?.advice || '' } : {}),
					...(['frequency'].includes(type) ? {} : { status: details?.status || 'active' }),
				}}
				validationSchema={getAllopathyValidationSchema({ t, type, name: getNameFieldLabel })}
				enableReinitialize={true}
				onSubmit={handleSubmit}
			>
				{({ isSubmitting }) => (
					<FormikForm>
						<Modal.Body>
							<Row className="mb-3">
								<Form.Group>
									<Form.Label>{getNameFieldLabel} <RequiredAstric /></Form.Label>
									<Field name="name" as={commaSeparatedAllopathyTypes.includes(type) && !isEdit ? 'textarea' : Form.Control} className="form-control" />
									<div className="text-danger small"><ErrorMessage name="name" /></div>
								</Form.Group>
							</Row>

							{['advice', 'diet-exerise'].includes(type) && (
								<Row className="mb-3">
									<Form.Group>
										<Form.Label>{t('ALLOPATHY.NAV.ADVICE')} <RequiredAstric /></Form.Label>
										<Field name="advice" as="textarea" className="form-control" />
										<div className="text-danger small"><ErrorMessage name="advice" /></div>
									</Form.Group>
								</Row>
							)}

							{!commaSeparatedAllopathyTypes.includes(type) && (
								<Row className="mb-3">
									<Form.Group>
										<Form.Label>{t('GENERAL.STATUS')}</Form.Label>
										<Field name="status" as="select" className="form-select">
											<option value="active">{t('GENERAL.ACTIVE')}</option>
											<option value="inactive">{t('GENERAL.INACTIVE')}</option>
										</Field>
										<div className="text-danger small"><ErrorMessage name="status" /></div>
									</Form.Group>
								</Row>
							)}

							<Row className="mb-3">
								<Col>
									<Button type="submit" className='px-3 py-2 me-2' variant="primary" disabled={isSubmitting}>
										{isSubmitting ? t('GENERAL.SAVING') : isEdit ? t('GENERAL.UPDATE') : t('GENERAL.SUBMIT')}
									</Button>
									<Button variant="danger" className='px-3 py-2' onClick={handleClose}>{t('GENERAL.CANCEL')}</Button>
								</Col>
							</Row>
						</Modal.Body>
					</FormikForm>
				)}
			</Formik>
		</Modal >
	);
};

export default AllopathyModel;
